import { isUuid, stripEntitySelectionQueryParams } from '@joyful/utils'
import { getPathOrganizationScope } from './getPathOrganizationScope'

export function addOrganizationScopeToPath({
  orgSlug,
  path,
}: {
  orgSlug: string
  path: string
}) {
  const { pathname, suffix } = splitPathFromSuffix(path)
  const scope = getPathOrganizationScope(pathname)

  if (scope === 'missing-org') {
    return `/${orgSlug}${path}`
  }

  if (scope === 'scoped') {
    const rescopedPathname = replaceOrgSlugInPathname({ orgSlug, pathname })
    const strippedPathname = stripTrailingUuidSegments(rescopedPathname)
    if (strippedPathname !== rescopedPathname) {
      return strippedPathname
    }

    const cleanedSuffix = stripEntitySelectionQueryParams({
      pathname: strippedPathname,
      suffix,
    })
    return `${strippedPathname}${cleanedSuffix}`
  }

  return path
}

function splitPathFromSuffix(path: string) {
  const queryStartIndex = path.indexOf('?')
  const hashStartIndex = path.indexOf('#')
  const suffixStartIndex = getSuffixStartIndex({
    hashStartIndex,
    queryStartIndex,
  })

  if (suffixStartIndex === null) {
    return { pathname: path, suffix: '' }
  }

  return {
    pathname: path.slice(0, suffixStartIndex),
    suffix: path.slice(suffixStartIndex),
  }
}

function getSuffixStartIndex({
  hashStartIndex,
  queryStartIndex,
}: {
  hashStartIndex: number
  queryStartIndex: number
}) {
  if (queryStartIndex === -1 && hashStartIndex === -1) {
    return null
  }

  if (queryStartIndex === -1) {
    return hashStartIndex
  }

  if (hashStartIndex === -1) {
    return queryStartIndex
  }

  return Math.min(queryStartIndex, hashStartIndex)
}

function replaceOrgSlugInPathname({
  orgSlug,
  pathname,
}: {
  orgSlug: string
  pathname: string
}) {
  const segments = pathname.split('/')
  const firstSegmentIndex = segments.findIndex((segment) => {
    return segment.length > 0
  })

  if (firstSegmentIndex === -1) {
    return `/${orgSlug}`
  }

  segments[firstSegmentIndex] = orgSlug
  return segments.join('/')
}

/**
 * Strips trailing UUID path segments from a path. When switching orgs,
 * entity-specific UUIDs (e.g. /projects/970f145a-...) won't exist in
 * the target org, so we trim them to navigate to the parent route instead.
 */
function stripTrailingUuidSegments(path: string) {
  const segments = path.split('/')
  while (segments.length > 1 && isUuid(segments.at(-1)) === true) {
    segments.pop()
  }

  return segments.join('/')
}
